import React from 'react'

import { FiRefreshCw, FiZoomIn, FiZoomOut } from 'react-icons/fi'
import { MdFullscreen, MdFullscreenExit } from 'react-icons/md'

import { HStack, IconButton, Tooltip } from '@chakra-ui/react'

const ICON_SIZES = {
  ZOOM_CONTROL: '20px',
  RESET_CONTROL: '18px',
  FULLSCREEN_CONTROL: '24px',
} as const

interface ZoomControlIProps {
  label: string
  icon: React.ElementType
  onClick: () => void
  isDisabled: boolean
  iconSize: string
}

interface ZoomStateIProps {
  canZoomIn: boolean
  canZoomOut: boolean
  isZoomed: boolean
  shouldShowControls: boolean
}

interface ZoomActionsIProps {
  onZoomIn: () => void
  onZoomOut: () => void
  onReset: () => void
}

interface MediaPreviewerControlsIProps {
  modalSize: '3xl' | 'full'
  onToggleModalSize: () => void
  zoomActions: ZoomActionsIProps
  zoomState: ZoomStateIProps
}

const createZoomControls = (
  canZoomIn: boolean,
  canZoomOut: boolean,
  isZoomed: boolean,
  onZoomIn: () => void,
  onZoomOut: () => void,
  onZoomReset: () => void
): readonly ZoomControlIProps[] =>
  [
    {
      label: 'Zoom out',
      icon: FiZoomOut,
      onClick: onZoomOut,
      isDisabled: !canZoomOut,
      iconSize: ICON_SIZES.ZOOM_CONTROL,
    },
    {
      label: 'Reset zoom',
      icon: FiRefreshCw,
      onClick: onZoomReset,
      isDisabled: !isZoomed,
      iconSize: ICON_SIZES.RESET_CONTROL,
    },
    {
      label: 'Zoom in',
      icon: FiZoomIn,
      onClick: onZoomIn,
      isDisabled: !canZoomIn,
      iconSize: ICON_SIZES.ZOOM_CONTROL,
    },
  ] as const

const renderZoomButton = ({
  label,
  icon: Icon,
  onClick,
  isDisabled,
  iconSize,
}: ZoomControlIProps) => (
  <Tooltip key={label} label={label}>
    <IconButton
      _hover={{ bg: 'gray.100' }}
      aria-label={label}
      icon={<Icon size={iconSize} />}
      isDisabled={isDisabled}
      onClick={onClick}
      size='sm'
      variant='ghost'
    />
  </Tooltip>
)

const renderFullscreenButton = (
  modalSize: '3xl' | 'full',
  onToggleModalSize: () => void
) => {
  const isNormalSize = modalSize === '3xl'
  const label = isNormalSize
    ? 'Expand to full screen'
    : 'Collapse to normal size'
  const ariaLabel = isNormalSize ? 'Expand modal' : 'Collapse modal'
  const Icon = isNormalSize ? MdFullscreen : MdFullscreenExit

  return (
    <Tooltip label={label}>
      <IconButton
        _hover={{ bg: 'gray.100' }}
        aria-label={ariaLabel}
        icon={<Icon size={ICON_SIZES.FULLSCREEN_CONTROL} />}
        onClick={onToggleModalSize}
        size='sm'
        variant='ghost'
      />
    </Tooltip>
  )
}

export const MediaPreviewerControls = ({
  modalSize,
  zoomState,
  zoomActions,
  onToggleModalSize,
}: MediaPreviewerControlsIProps) => {
  const { canZoomIn, canZoomOut, isZoomed, shouldShowControls } = zoomState
  const { onZoomIn, onZoomOut, onReset } = zoomActions

  const zoomControls = createZoomControls(
    canZoomIn,
    canZoomOut,
    isZoomed,
    onZoomIn,
    onZoomOut,
    onReset
  )

  return (
    <HStack pos='absolute' right={12} spacing={1} top={2} zIndex={2}>
      {shouldShowControls && zoomControls.map(renderZoomButton)}
      {renderFullscreenButton(modalSize, onToggleModalSize)}
    </HStack>
  )
}
